<template>
    <div class="w-full flex flex-col gap-6 justify-start">
        <!-- Header Section -->
        <div class="flex justify-between items-center">
            <div class="text-white">
                <p class="font-bold text-2xl">//Storages</p>
                <p>Manage your storage providers or configure a new storage node.</p>
            </div>
            <div class="cursor-pointer">
                <vx-tooltip text="/ storage.create" position="left">
                    <button class="outline-none focus:outline-none border-none cursor-pointer"
                        @click="openCreateStorageModal">
                        <img src="@/assets/images/pixel_create.svg" alt="Add Storage" />
                    </button>
                </vx-tooltip>
            </div>
        </div>

        <!-- Storage Providers Table -->
        <div class="w-full">
            <div class="cast_list_cont">
                <!-- Loading State -->
                <div v-if="isLoading">
                    <StorageCardShimmer />
                    <StorageCardShimmer :style="{ opacity: 0.6 }" />
                    <StorageCardShimmer :style="{ opacity: 0.5 }" />
                </div>

                <!-- Empty State -->
                <div v-else-if="myStorages.length === 0"
                    class="flex flex-row h-full items-start justify-center text-white text-left pt-6">
                    <span class="text-2xl font-semibold text-white">You have not configured any storages yet.
                        <br>
                        Click <span class="text-primary cursor-pointer" @click="openCreateStorageModal">here</span> to get
                        started and configure your storage
                        now!</span>
                </div>

                <!-- Storage Providers Table -->
                <div v-else>
                    <div class="storage-grid">
                        <div v-for="storage in myStorages" :key="storage.id">
                            <StorageCard :storage="storage" :index="index" :fetchStorages="fetchStorages" />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Create Storage Modal -->
        <CreateStorageModal v-if="activeModal === 'createStorageModal'" :closeModal="closeCreateStorageModal"
            :providers="providers" @add-storage="handleAddStorage" :fetchStorages="fetchStorages" />
    </div>
</template>

<script>
import SimpleMenu from '@/components/common/simpleMenu/SimpleMenu.vue';
import StorageCardShimmer from './components/StorageCardShimmer.vue';
import CreateStorageModal from './components/CreateStorageModal.vue';
import EditStorageModal from './components/EditStorageModal.vue';
import DeleteStorageModal from './components/DeleteStorageModal.vue';
import StorageCard from './components/StorageCard.vue';
import constants from '../../../../constant';
import api from '@/http/api/api.js'

export default {
    name: 'StorageSection',
    components: {
        SimpleMenu,
        StorageCardShimmer,
        CreateStorageModal,
        EditStorageModal,
        DeleteStorageModal,
        StorageCard,
    },
    data() {
        return {
            isLoading: false,
            activeModal: null,
            selectedStorage: null,
            storageToDelete: null,
            myStorages: [],
            providers: [],
            storageMenu: [
                {
                    label: "Edit",
                    icon: () => import("@/assets/svgs/menu-icons/edit.vue"),
                    onClick: () => this.openEditStorageModal(),
                },
                {
                    label: "Delete",
                    icon: () => import("@/assets/svgs/menu-icons/delete.vue"),
                    onClick: () => this.openDeleteStorageModal(),
                },
                {
                    label: "Explorer",
                    icon: () => import("@/assets/svgs/menu-icons/folder.vue"),
                    onClick: () => this.openFileExplorer(),
                },
            ]
        };
    },
    created() {
        this.fetchStorages();
    },
    methods: {
        async fetchStorages() {
            this.isLoading = true;
            try {
                const storagesResponse = await api.get(`${constants.apiCastUrl}/api/v1/storageproxy/storages`);

                const storages = storagesResponse?.data?.data || [];

                this.myStorages = storages.map(storage => ({
                    ...storage,
                    options: {
                        apiUrl: storage?.options?.nodeUrl || '',
                        nodePassword: storage?.options?.apiPassword || '',
                        apiKey: storage?.options?.apiPassword || '',
                    },
                }));
            } catch (error) {
                console.error('Error fetching storages:', error?.response?.data || error.message || error);
                this.$vs.notify({
                    title: 'Error',
                    text: 'Failed to load storages. Please try again.',
                    color: 'danger',
                });
            } finally {
                this.isLoading = false;
            }
        },
        openCreateStorageModal() {
            this.activeModal = 'createStorageModal';
        },
        closeCreateStorageModal() {
            this.activeModal = null;
        },
        openEditStorageModal(storage) {
            this.selectedStorage = storage;
            this.activeModal = 'editStorageModal';
        },
        closeEditStorageModal() {
            this.activeModal = null;
            this.selectedStorage = null;
        },
        closeStorageModal() {
            this.activeModal = null;
        },
        async handleUpdateStorage(updatedStorage) {
            try {

                this.myStorages = this.myStorages.map((storage) =>
                    storage.id === updatedStorage.id
                        ? {
                            ...storage,
                            name: updatedStorage.name,
                            provider: this.providers.find((p) => p.name === updatedStorage.provider) || storage.provider,
                            options: {
                                apiUrl: updatedStorage.options.apiUrl,
                                nodePassword: updatedStorage.options.nodePassword,
                                apiKey: updatedStorage.options.apiKey,
                            },
                            selfHost: updatedStorage.selfHost,
                            deActive: updatedStorage.deActive,
                            status: updatedStorage.status,
                            updatedAt: new Date().toISOString(),
                        }
                        : storage
                );
                this.$vs.notify({
                    title: 'Success',
                    text: 'Storage provider updated successfully!',
                    color: 'success',
                });
            } catch (error) {
                console.error('Error updating storage:', error);
                this.$vs.notify({
                    title: 'Error',
                    text: 'Failed to update storage provider.',
                    color: 'danger',
                });
            }

            this.closeEditStorageModal();
        },
        async handleAddStorage(newStorage) {
            try {
                const newId = this.myStorages.length
                    ? Math.max(...this.myStorages.map((s) => s.id)) + 1
                    : 1;

                const selectedProvider = this.providers.find((p) => p.name === newStorage.provider);

                const storageToAdd = {
                    id: newId,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                    name: newStorage.name,
                    provider: selectedProvider || {
                        name: newStorage.provider,
                        type: 'unknown',
                    },
                    options: {
                        apiUrl: newStorage.options.apiUrl || '',
                        nodePassword: newStorage.options.nodePassword || '',
                        apiKey: newStorage.options.apiKey || '',
                    },
                    selfHost: newStorage.selfHost || false,
                    deActive: newStorage.deActive || false,
                    status: newStorage.status || false,
                };

                this.myStorages.push(storageToAdd);

                this.$vs.notify({
                    title: 'Success',
                    text: 'Storage provider added successfully!',
                    color: 'success',
                });
            } catch (error) {
                console.error('Error adding storage:', error);
                this.$vs.notify({
                    title: 'Error',
                    text: 'Failed to add storage provider.',
                    color: 'danger',
                });
            }
        },
        openDeleteStorageModal(storage) {
            this.selectedStorage = storage;
            this.activeModal = 'deleteStorageModal';
        },
        closeDeleteStorageModal() {
            this.activeModal = null;
            this.selectedStorage = null;
        },
        async deleteStorage() {
            if (!this.selectedStorage) {
                this.$vs.notify({
                    title: 'Error',
                    text: 'No storage selected.',
                    color: 'danger',
                });
                this.closeDeleteStorageModal();
                return;
            }

            try {
                await api.delete(`${constants.apiCastUrl}/api/v1/storageproxy/storages/${this.selectedStorage.id}`);

                await this.fetchStorages();

                this.$vs.notify({
                    title: 'Success',
                    text: `Storage "${this.selectedStorage.name}" deleted successfully!`,
                    color: 'success',
                });
            } catch (error) {
                console.error('Error deleting storage:', error);
                this.$vs.notify({
                    title: 'Error',
                    text: 'Failed to delete storage. Please try again.',
                    color: 'danger',
                });
            } finally {
                this.closeDeleteStorageModal();
            }
        },
        openFileExplorer(id) {
            console.log(`Open file explorer for storage ID: ${id}`);
        },
    },
};
</script>

<style scoped>
* {
    font-family: 'JetBrains Mono', monospace !important;
}

.storage-page {
    background-color: #0e0e11;
    color: #e6e6e6;
    font-family: 'Courier New', Courier, monospace;
    padding: 2rem;
}

.storage-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    margin-bottom: 2rem;
}

.add-btn {
    background: #ffffff11;
    border: 2px solid #e6e6e6;
    color: #e6e6e6;
    padding: 0.5rem 1rem;
    border-radius: 4px;
    font-weight: bold;
    cursor: pointer;
    transition: 0.2s;
}

.add-btn span {
    font-size: 1.25rem;
    margin-right: 0.5rem;
}

.add-btn:hover {
    background-color: #ffffff22;
}

.storage-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(380px, 2fr));
    gap: 1.5rem;
}

.storage-card {
    border-top: 1px solid #FFFFFF;
    border-left: 1px solid #FFFFFF;
    border-right: 3px solid #FFFFFF;
    border-bottom: 3px solid #FFFFFF;
    background: #0A0B0F;
    padding: 1.5rem;
    transition: 0.3s;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
}

.custm-more-icon {
    background-color: transparent !important;
    border: none !important;
}

.status-container {
    padding: 5px 10px;
    border-radius: 20px;
    display: flex;
    align-items: center;
}

.status-container-conn {
    color: #22C55E;
    background-color: #22C55E20;
}

.status-container-disconn {
    color: #EF4444;
    background-color: #EF444420;
}

.storage-card h3 {
    color: #FFFFFF;
    margin-bottom: 0.5rem;
}

.storage-label-light {
    color: #545454;
}

.status {
    display: flex;
    align-items: center;
    margin-bottom: 0.5rem;
    display: flex;
    justify-content: space-between;
}

.status-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    margin-right: 8px;
}

.connected-dot {
    background-color: #22C55E;
}

.disconnected-dot {
    background-color: #EF4444;
}

.enabled {
    color: #22c55e;
}

.disabled {
    color: #a3a3a3;
}

.provider {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 1rem;
    font-weight: bold;
    border-top: 1px solid #545454;
}

.actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 1rem;
}

.actions button {
    background: transparent;
    border: none;
    cursor: pointer;
}

.actions img {
    width: 24px;
    height: 24px;
}



.cast_list_cont {
    max-height: 60vh;
    overflow-y: auto;
}

.cast_list_cont::-webkit-scrollbar {
    display: none;
}

.cast_list_cont::-webkit-scrollbar-thumb {
    display: none;
}

.cast_list_cont::-webkit-scrollbar-track {
    background-color: #272727;
}

table {
    border: 1px solid white;
}

th,
td {
    text-align: left;
    font-size: 14px;
    border: 1px solid white;
}

th {
    font-weight: 500;
    background-color: #272727;
}
</style>